/** @jsx jsx */

import { useQueryClient } from 'react-query';
import { Fragment, useState } from 'react';

import { Icon } from '@zapier/zinnia-icons';

import {
  Spinner,
  Tooltip,
  TooltipFocusableTrigger,
  Table,
  TableBody,
  TableRow,
  TableTd,
  Text,
  ToggleSwitch,
} from '@zapier/design-system';

import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import {
  useNotify,
  useQuerySelfSignupDomains,
  useUpdateSelfSignupDomains,
} from 'app/developer-v3/hooks';

import SettingsModal from './SettingsModal';

const Styles = {
  alertTriangle: css`
    margin-top: 6px;
  `,
  settingText: css`
    display: flex;
    align-items: center;
    line-height: 1;
    margin: 15px 0px;
    gap: 5px;
  `,
  tooltip: css`
    display: inline-block;
    min-width: 280px;
  `,
  toggleWrapper: css`
    display: flex;
    justify-content: center;
    width: 30px;
  `,
};

type Props = {
  params: { appId: string };
};

function Settings(props: Props) {
  const {
    params: { appId },
  } = props;

  const [isShowingModal, setIsShowingModal] = useState(false);

  const notify = useNotify();
  const queryClient = useQueryClient();

  const {
    domains,
    isAllowed,
    isLoading: isQuerying,
    queryKey,
  } = useQuerySelfSignupDomains(appId);

  const { isMutating, mutate: updateDomains } = useUpdateSelfSignupDomains(
    appId,
    {
      onError: () => {
        notify.failure('Unable to update setting.');
      },
      onSuccess: async ({ self_signup_allowed }) => {
        await queryClient.invalidateQueries(queryKey);
        notify.success(
          `Setting ${self_signup_allowed ? 'enabled' : 'disabled'}!`
        );
      },
    }
  );

  return (
    <Fragment>
      <Table>
        <TableBody>
          <TableRow>
            <TableTd>
              <div css={Styles.settingText}>
                <Text fontWeight={600}>
                  Allow team members with specific email domains to join your
                  integration as a Collaborator
                </Text>
                <Tooltip
                  allowMultiline={true}
                  content="Anyone with an email address associated with an approved email domain can join your integrations as a Collaborator on their own, without waiting for an admin invitation."
                >
                  <TooltipFocusableTrigger>
                    <Icon color="GrayWarm7" name="alertCircleFill" size={16} />
                  </TooltipFocusableTrigger>
                </Tooltip>
              </div>
              <div css={Styles.settingText}>
                <Text margin="6px 0 0" tag="p">
                  <Link
                    onClick={() => {
                      setIsShowingModal(true);
                    }}
                    style={{ textDecoration: 'underline' }}
                  >
                    Edit email domains
                  </Link>{' '}
                  team members can use to join.
                </Text>
                {!isQuerying && domains.length === 0 && (
                  <Fragment>
                    <span css={Styles.alertTriangle}>
                      <Icon
                        color="StatusErrorWeaker"
                        name="alertTriangleFill"
                        size={16}
                      />
                    </span>
                    <Text color="StatusErrorWeaker" margin="6px 0 0" tag="p">
                      No email domains have been added.
                    </Text>
                  </Fragment>
                )}
              </div>
            </TableTd>
            <TableTd>
              <div css={Styles.toggleWrapper}>
                {isQuerying || isMutating ? (
                  <Spinner />
                ) : (
                  <ToggleSwitch
                    checked={isAllowed}
                    onChange={() => updateDomains({ allowed: !isAllowed })}
                  />
                )}
              </div>
            </TableTd>
          </TableRow>
        </TableBody>
      </Table>
      {isShowingModal && (
        <SettingsModal appId={appId} onClose={() => setIsShowingModal(false)} />
      )}
    </Fragment>
  );
}

export default Settings;
